// src/App.js - With Google and Microsoft authentication support
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { googleLogout } from '@react-oauth/google';
import useUserProfile from './hooks/useUserProfile';
import useAuthentication from './hooks/useAuthentication';
import EmployeePortal from './components/EmployeePortal';
import RegistrationAndLanding from './components/RegistrationAndLanding';
import EnhancedRegistration from './components/EnhancedRegistration';
import SiriusConnect365Licenses from './components/SiriusConnect365Licenses';
import DebugPanel from './components/common/DebugPanel';
import EnvironmentBadge from './components/common/EnvironmentBadge';

export default function App() {
  const { instance, accounts } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const isGoogleAuthenticated = localStorage.getItem('authProvider') === 'google' && localStorage.getItem('googleCredential');
  const isAuthenticated = isMsalAuthenticated || isGoogleAuthenticated;
  const authProvider = localStorage.getItem('authProvider') || (isMsalAuthenticated ? 'microsoft' : null);

  const { token, isLoading: tokenLoading, error: tokenError } = useAuthentication();
  const { employeeNo, isLoading: profileLoading } = useUserProfile();

  // Log auth status
  console.log("App component rendered. isAuthenticated:", isAuthenticated, "authProvider:", authProvider, "accounts:", accounts);
  // Log app initialization in development
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('%c📱 App Component Mounted', 'background: #8b5cf6; color: white; padding: 4px 8px; font-weight: bold; border-radius: 3px;');
      console.log('Is Authenticated:', isAuthenticated);
      console.log('Accounts:', accounts);
      console.log('Token Loading:', tokenLoading);
      console.log('Profile Loading:', profileLoading);
      if (token) {
        console.log('%c✅ Token available', 'color: #10b981; font-weight: bold');
      }
    }
  }, [isAuthenticated, accounts, token, tokenLoading, profileLoading]);

  // Block rendering if not authenticated (shouldn't happen with AuthenticatedTemplate)
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        <p className="mt-4 text-indigo-600 font-semibold">Loading...</p>
      </div>
    );
  }

  // Check auth provider
  const isGoogleAuth = authProvider === 'google';

  // Handle logout - supports both Google and Microsoft
  const handleLogout = () => {
    const provider = localStorage.getItem('authProvider');

    // Clear all auth data
    sessionStorage.clear();
    localStorage.clear();

    if (provider === 'google') {
      // Google logout
      googleLogout();
      window.location.href = '/';
    } else {
      // Microsoft logout
      instance.logoutRedirect();
    }
  };

  // Show loading state while fetching token (Microsoft only) or employee profile (both)
  if ((!isGoogleAuth && tokenLoading) || profileLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        <p className="mt-4 text-indigo-600 font-semibold">
          {tokenLoading ? 'Authenticating...' : 'Loading your profile...'}
        </p>
      </div>
    );
  }

  // Show error if token acquisition failed (Microsoft auth only)
  if (!isGoogleAuth && tokenError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-6">{tokenError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-indigo-600 text-white py-2 px-6 rounded-lg hover:bg-indigo-700 font-semibold transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show error if user is not an employee (both auth providers)
  if (employeeNo === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            Sorry, you are not registered as an employee of this company.
            Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={handleLogout}
            className="bg-indigo-600 text-white py-2 px-6 rounded-lg hover:bg-indigo-700 font-semibold transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and is an employee - show the app
  return (
    <>
      <Routes>
        {/* Public routes - redirect to portal if already authenticated */}
        <Route path="/" element={<Navigate to="/portal" replace />} />
        <Route path="/register" element={<Navigate to="/portal" replace />} />
        <Route path="/pricing" element={<Navigate to="/portal" replace />} />

        {/* Main employee portal */}
        <Route path="/portal/*" element={<EmployeePortal onLogout={handleLogout} />} />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/portal" replace />} />
      </Routes>

      {/* Debug panel and environment badge only shown in development */}
      {process.env.NODE_ENV === 'development' && (
        <>
          <DebugPanel />
          <EnvironmentBadge />
        </>
      )}

      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover draggable />
    </>
  );
}
